import { Can } from '@/components/can/Can';
import { Button } from '@/design-system/button';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import PremiumStore from '@/features/store-management/premium-stores';
import { usePremiumStoresList } from '@/features/store-management/premium-stores/hooks/usePremiumStoresList';
import { encryptedStatusTypeMap } from '@/features/store-management/premium-stores/premiumStores.constant';
import { Translation } from '@/hoslog-intl';
import { PlusIcon } from '@/icons/plus-icon';
import { createFileRoute, Link, SearchSchemaInput, useSearch } from '@tanstack/react-router';
import { z } from 'zod';

const sortItemSchema = z.object({
  id: z.enum([
    'storeNameEng',
    'telephone',
    'clubCategory',
    'prefecture',
    'totalRatings',
    'status',
    'createdDate',
    'updatedDate',
    'emailAddress',
  ]),
  desc: z.boolean(),
});

const PremiumStoreSearchSchema = z
  .object({
    sort: z
      .preprocess((val) => {
        if (typeof val === 'string') {
          try {
            return JSON.parse(val);
          } catch {
            return undefined;
          }
        }
        return val;
      }, z.array(sortItemSchema))
      .optional(),

    createdDate: z.string().optional(),

    q: z
      .any()
      .transform((val) => (val == null ? undefined : String(val)))
      .optional(),

    status: z.string().optional(),
    locationFilter: z.string().optional(),

    page: z.coerce.number().positive().catch(0),
    activeTab: z.enum(['A', 'P', 'R']).default('A'),
    fetch: z.coerce.number().positive().max(100).catch(10),
  })
  .transform((data) => {
    return {
      ...data,
      statusType:
        data.activeTab === 'P'
          ? encryptedStatusTypeMap.pending
          : data.activeTab === 'A'
            ? encryptedStatusTypeMap.approved
            : encryptedStatusTypeMap.rejected,
    };
  });

export const Route = createFileRoute('/(app)/store-information/premium-stores/')({
  component: RouteComponent,

  loader: () => {
    const renderActions = (
      <Can rule={{ allOf: [PERMS.club.premium.create] }}>
        <Link
          to="/store-information/premium-stores/add-premium-store"
          search={() => ({ breadcrumbReturnTo: window.location.href })}
        >
          <Button type="submit" className="w-[110px]">
            <PlusIcon /> <Translation id="common.page.button.addNew" />
          </Button>
        </Link>
      </Can>
    );

    return {
      actions: { renderActions },
    };
  },
  validateSearch: (search: SearchSchemaInput) => {
    return PremiumStoreSearchSchema.parse(search);
  },
});

function RouteComponent() {
  const { activeTab } = useSearch({ from: '/(app)/store-information/premium-stores/' });
  const { premiumStores, isLoading, meta } = usePremiumStoresList(activeTab);
  return <PremiumStore data={premiumStores} isLoading={isLoading} pagination={meta} />;
}
